// src/components/maintenance/MaintenancePage.jsx
//
// Public maintenance screen. Colored "bullet" lines fly in random directions
// over a softly shifting color background, with "Genius at work.." centered.
// A hidden hotspot in the bottom-right corner opens the admin login form on
// SHIFT + CLICK. Successful admin login stores a token (sessionStorage) and
// calls onUnlock() so the gate re-renders the full site for this admin.

import React, { useRef, useEffect, useState, useCallback } from "react";
import { API_ENDPOINTS } from "../../config/apiConfig";
import { saveAdminToken } from "./adminAuth";
import "./MaintenancePage.css";

export default function MaintenancePage({ onUnlock, message }) {
  const canvasRef = useRef(null);
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ---- Flying colored "bullet" lines ----
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationId;
    let bullets = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const rand = (min, max) => Math.random() * (max - min) + min;

    const spawn = () => {
      const angle = rand(0, Math.PI * 2);
      const speed = rand(6, 16);
      return {
        x: rand(0, canvas.width),
        y: rand(0, canvas.height),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        len: rand(40, 140),
        width: rand(1, 2.5),
        life: 0,
        maxLife: rand(60, 160),
        hue: rand(0, 360),
        hueShift: rand(0.5, 2),
      };
    };

    for (let i = 0; i < 40; i++) bullets.push(spawn());

    const tick = () => {
      // Transparent clear so the color-cycling background shows through.
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      bullets.forEach((b, i) => {
        const nx = b.x + b.vx;
        const ny = b.y + b.vy;

        const mag = Math.hypot(b.vx, b.vy) || 1;
        const tx = b.x - (b.vx / mag) * b.len;
        const ty = b.y - (b.vy / mag) * b.len;

        b.hue = (b.hue + b.hueShift) % 360;

        const grad = ctx.createLinearGradient(tx, ty, nx, ny);
        grad.addColorStop(0, `hsla(${b.hue}, 100%, 70%, 0)`);
        grad.addColorStop(1, `hsla(${b.hue}, 100%, 70%, 0.95)`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = b.width;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(nx, ny);
        ctx.stroke();

        b.x = nx;
        b.y = ny;
        b.life++;

        const off =
          b.x < -b.len || b.x > canvas.width + b.len ||
          b.y < -b.len || b.y > canvas.height + b.len ||
          b.life > b.maxLife;

        if (off) bullets[i] = spawn();
      });

      animationId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ---- Admin unlock via backend ----
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_LOGIN, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });
        const data = await res.json();

        if (res.status === 200 && data.status === "success" && data.token) {
          saveAdminToken(data.token);
          onUnlock();
        } else if (res.status === 403) {
          setError("This account has been disabled.");
        } else if (res.status === 401) {
          setError("Invalid username or password.");
        } else {
          setError("Something went wrong, try again.");
        }
      } catch {
        setError("Server unreachable. Try again.");
      } finally {
        setLoading(false);
      }
    },
    [username, password, onUnlock]
  );

  return (
    <div className="maint-root">
      <canvas ref={canvasRef} className="maint-canvas" />
      <div className="maint-bg-cycle" />

      <div className="maint-center">
        <h1 className="maint-title">{message || "Genius at work.."}</h1>
      </div>

      {/* Hidden unlock hotspot, bottom-right corner — SHIFT + CLICK to open */}
      <button
        className="maint-secret"
        aria-label="admin"
        onClick={(e) => {
          if (e.shiftKey) setShowLogin((s) => !s);
        }}
      />

      {showLogin && (
        <div className="maint-modal-overlay" onClick={() => setShowLogin(false)}>
          <form
            className="maint-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
          >
            <h2>Restricted</h2>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && <div className="maint-error">{error}</div>}
            <button type="submit" className="maint-enter" disabled={loading}>
              {loading ? "Checking…" : "Enter"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
